import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true); setError("");
    const r = await register({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.password,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success(`Welcome aboard, ${r.user.name}`);
    navigate(nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : "/dashboard", { replace: true });
  };

  return (
    <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl grid-cols-1 items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-2">
      <Card className="order-2 mx-auto w-full max-w-md border-border/70 card-elevated lg:order-1">
        <CardContent className="p-8">
          <div className="mb-6"><BrandLogo /></div>
          <h1 className="font-display text-2xl font-bold">Create your PilotYatra account</h1>
          <p className="mt-1 text-sm text-muted-foreground">Start with free sample practice. Upgrade anytime.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={onChange("name")} data-testid="register-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={onChange("email")} data-testid="register-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={onChange("password")} data-testid="register-password-input" className="mt-1.5" />
              <div className="mt-1 text-xs text-muted-foreground">Minimum 6 characters.</div>
            </div>
            {error && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="register-submit-btn">
              {loading ? "Creating..." : "Create account"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to={`/login${location.search}`} className="font-semibold text-[hsl(var(--brand-navy))] hover:underline" data-testid="go-to-login">
              Log in
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="order-1 hidden lg:order-2 lg:block">
        <div className="brand-gradient relative overflow-hidden rounded-3xl p-10 text-white card-elevated">
          <div className="font-display text-xs font-bold uppercase tracking-[0.25em] text-white/70">Join PilotYatra</div>
          <h2 className="mt-2 font-display text-3xl font-bold leading-tight">
            One honest question bank.<br />Three focused modes.
          </h2>
          <ul className="mt-6 space-y-3 text-sm text-white/85">
            <li>· Learn with instant explanations</li>
            <li>· Train under exam-grade timers</li>
            <li>· Hunt down your weak spots in Revision</li>
          </ul>
          <div className="absolute -right-6 -bottom-6 h-56 w-56 rounded-full bg-[hsl(var(--brand-orange))]/25 blur-3xl" />
        </div>
      </div>
    </div>
  );
}
